import { HttpErrorResponse } from "@angular/common/http";
import type {
  PlatformAccessAssignmentRevokeRequest as GeneratedPlatformAccessAssignmentRevokeRequest,
  PlatformAccessAssignmentsDetail as GeneratedPlatformAccessAssignmentsDetail,
  PlatformNavigationNodeCreateRequest as GeneratedPlatformNavigationNodeCreateRequest,
  PlatformNavigationNodeDetail as GeneratedPlatformNavigationNodeDetail,
  PlatformNavigationNodeUpdateRequest as GeneratedPlatformNavigationNodeUpdateRequest,
  PlatformNavigationRuleDraft as GeneratedPlatformNavigationRuleDraft,
  PlatformNavigationRuleRequest as GeneratedPlatformNavigationRuleRequest,
  PlatformPermissionCatalogItem as GeneratedPlatformPermissionCatalogItem,
  PlatformPermissionGrant as GeneratedPlatformPermissionGrant,
  PlatformPermissionGrantCreateRequest as GeneratedPlatformPermissionGrantCreateRequest,
  PlatformRoleAssignment as GeneratedPlatformRoleAssignment,
  PlatformRoleAssignmentCreateRequest as GeneratedPlatformRoleAssignmentCreateRequest,
  PlatformRoleCatalogItem as GeneratedPlatformRoleCatalogItem,
  PlatformUserProfileUpdateRequest as GeneratedPlatformUserProfileUpdateRequest,
  PlatformScopeTypeCatalogItem as GeneratedPlatformScopeTypeCatalogItem,
  PlatformScopeValueCatalogItem as GeneratedPlatformScopeValueCatalogItem,
  PlatformScopeValueCreateRequest as GeneratedPlatformScopeValueCreateRequest,
  PlatformScopeValueUpdateRequest as GeneratedPlatformScopeValueUpdateRequest,
} from "@app/generated/contracts";

export type PlatformAccessAssignmentsDetail = GeneratedPlatformAccessAssignmentsDetail;
export type PlatformRoleAssignment = GeneratedPlatformRoleAssignment;
export type PlatformPermissionGrant = GeneratedPlatformPermissionGrant;
export type PlatformRoleAssignmentCreateRequest = GeneratedPlatformRoleAssignmentCreateRequest;
export type PlatformPermissionGrantCreateRequest = GeneratedPlatformPermissionGrantCreateRequest;
export type PlatformRoleCatalogItem = GeneratedPlatformRoleCatalogItem;
export type PlatformUserProfileUpdateRequest = GeneratedPlatformUserProfileUpdateRequest;
/**
 * Catalog `scopeRequired` is legacy metadata retained for API compatibility.
 * It must not block the global role and permission assignments supported by Access management.
 */
export type PlatformPermissionCatalogItem = GeneratedPlatformPermissionCatalogItem;
export type PlatformScopeTypeCatalogItem = GeneratedPlatformScopeTypeCatalogItem;
export type PlatformScopeValueCatalogItem = GeneratedPlatformScopeValueCatalogItem;
export type PlatformScopeValueCreateRequest = GeneratedPlatformScopeValueCreateRequest;
export type PlatformScopeValueUpdateRequest = GeneratedPlatformScopeValueUpdateRequest;
export type PlatformAccessAssignmentRevokeRequest = GeneratedPlatformAccessAssignmentRevokeRequest;
export type PlatformNavigationNodeDetail = GeneratedPlatformNavigationNodeDetail;
export type PlatformNavigationRuleDraft = GeneratedPlatformNavigationRuleDraft;
export type PlatformNavigationNodeCreateRequest = GeneratedPlatformNavigationNodeCreateRequest;
export type PlatformNavigationNodeUpdateRequest = GeneratedPlatformNavigationNodeUpdateRequest;
export type PlatformNavigationRuleRequest = GeneratedPlatformNavigationRuleRequest;

/**
 * One row of the PlatformUsers OData feed that the Access Management users grid renders.
 * POST /api/platform/access/users answers with the same shape for the user it created.
 */
export interface PlatformUserCatalogRow {
  id: number;
  displayName: string;
  email?: string | null;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  isActive: boolean;
  lastAuthenticatedUtc?: string | null;
}

export type PlatformUserCreated = PlatformUserCatalogRow;

/** How one effective permission reaches a user: through a role assignment or through a direct grant. */
export type PlatformEffectivePermissionSourceKind = "role" | "direct";

/**
 * One assignment that grants an effective permission. Role-derived sources carry the role
 * code and name; direct grants carry neither. The scope and window are those of the assignment.
 * Mirrors PlatformEffectivePermissionSourceDto in platform-access-admin.v1.schema.json.
 */
export interface PlatformEffectivePermissionSource {
  kind: PlatformEffectivePermissionSourceKind;
  roleCode?: string | null;
  roleName?: string | null;
  scopeTypeCode?: string | null;
  scopeKey?: string | null;
  effectiveStartUtc?: string | null;
  effectiveEndUtc?: string | null;
}

/** One permission the runtime evaluation grants, with every source kept rather than collapsed. */
export interface PlatformEffectivePermission {
  code: string;
  name: string;
  /** Legacy catalog metadata; global assignment controls must not gate on this flag. */
  scopeRequired: boolean;
  sources: PlatformEffectivePermissionSource[];
}

/**
 * Body of GET /api/platform/access/users/{userId}/effective-permissions: the same evaluation the
 * runtime authorization checks use, grouped by permission and sorted by code.
 */
export interface PlatformUserEffectivePermissions {
  userId: number;
  displayName: string;
  isActive: boolean;
  asOfUtc: string;
  permissions: PlatformEffectivePermission[];
}

/**
 * Body of POST /api/platform/access/users as the new user form sends it. The user signs in with
 * the email and the password; no identity-provider identity is part of the body. Validation
 * problems come back keyed by these property names (for example "displayName", "email",
 * "password" or "roleCodes").
 */
export interface PlatformUserCreateRequest {
  displayName: string;
  /** The address the user signs in with. */
  email: string;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  /** The new user form always sends true; a user is deactivated afterwards from the user details form. */
  isActive: boolean;
  /**
   * The password the user signs in with, sent exactly as entered. The API holds it to the
   * sign-in policy only (present and not only whitespace); it has no length or character rules.
   * The form's confirmation field is client-side only and never part of this body.
   */
  password: string;
  /**
   * The catalog roles the API assigns to the new user at creation, by code: at least one, none
   * blank, none twice, each an active role of the role catalog (the same roles the Roles tab
   * lists). Every one is assigned globally. Roles are added or removed on the Roles tab once the
   * user exists; ad hoc permissions are never part of creating a user.
   */
  roleCodes: string[];
}

export interface PlatformAssignmentFormValues {
  code: string;
  scopeTypeCode: string;
  scopeKey: string;
  effectiveStartUtc: string | Date | null;
  effectiveEndUtc: string | Date | null;
}

export interface PlatformNavigationNodeFormValues {
  nodeCode: string;
  parentNodeCode: string;
  nodeTypeCode: string;
  displayName: string;
  routePath: string;
  iconKey: string;
  groupCode: string;
  sortOrder: number | string | null;
  effectiveStartUtc: string | Date | null;
  effectiveEndUtc: string | Date | null;
}

export interface PlatformNavigationRuleFormValues {
  ruleTypeCode: string;
  permissionCode: string;
  featureFlagCode: string;
  visibilityModeCode: string;
  sortOrder: number | string | null;
  effectiveStartUtc: string | Date | null;
  effectiveEndUtc: string | Date | null;
}

/**
 * One issue of a mutation problem. For a validation problem `code` is the property path of
 * the failed field (for example "email" or "roleCode") and `detail` its message.
 */
export interface PlatformAccessAdminProblemIssue {
  code?: string;
  detail?: string;
}

export interface PlatformAccessAdminProblemDetails {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  problemCode?: string;
  issueCodes?: string[];
  issues?: PlatformAccessAdminProblemIssue[];
  correlationId?: string;
  errors?: Record<string, string[] | string>;
}

export interface PlatformAccessAdminDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
  problemCode?: string;
  issueCodes: string[];
  correlationId?: string | null;
  /**
   * Messages keyed by the property path the API reported them under (for example "email" or
   * "roleCode"), gathered from the mutation problem's `issues` and, for routes
   * that still send one, the `errors` dictionary, so a form can show each message beside its
   * field. Absent for errors that were not built from an HTTP response.
   */
  fieldMessages?: Record<string, string[]>;
}

export function mapPlatformAccessAdminError(error: unknown): PlatformAccessAdminDisplayError {
  if (error instanceof HttpErrorResponse) {
    const problem = toProblemDetails(error.error);
    const messages = new Set<string>();
    const fieldMessages = collectFieldMessages(problem);

    Object.values(fieldMessages).flat().forEach((message) => messages.add(message));

    if (typeof problem.detail === "string" && problem.detail.trim().length > 0) {
      messages.add(problem.detail.trim());
    }

    if (messages.size === 0) {
      messages.add(getDefaultErrorMessage(error.status));
    }

    return {
      title: normalizeText(problem.title) || getDefaultErrorTitle(error.status),
      messages: [...messages],
      statusCode: error.status || problem.status,
      problemCode: normalizeText(problem.problemCode) || undefined,
      issueCodes: Array.isArray(problem.issueCodes)
        ? problem.issueCodes.filter((code): code is string => normalizeText(code).length > 0)
        : [],
      correlationId: error.headers?.get("X-Correlation-ID") || normalizeText(problem.correlationId) || null,
      fieldMessages,
    };
  }

  return {
    title: "Unexpected error",
    messages: ["The request could not be completed. Try again or contact support if the problem persists."],
    issueCodes: [],
  };
}

function toProblemDetails(errorBody: unknown): PlatformAccessAdminProblemDetails {
  if (!errorBody || typeof errorBody !== "object" || Array.isArray(errorBody)) {
    return {};
  }

  return errorBody as PlatformAccessAdminProblemDetails;
}

function collectFieldMessages(problem: PlatformAccessAdminProblemDetails): Record<string, string[]> {
  const fieldMessages: Record<string, string[]> = {};
  const add = (field: string, message: string) => {
    const normalizedField = normalizeText(field);
    const normalizedMessage = normalizeText(message);
    if (normalizedField.length === 0 || normalizedMessage.length === 0) {
      return;
    }

    const existing = fieldMessages[normalizedField] ?? [];
    if (!existing.includes(normalizedMessage)) {
      fieldMessages[normalizedField] = [...existing, normalizedMessage];
    }
  };

  if (Array.isArray(problem.issues)) {
    for (const issue of problem.issues) {
      if (issue && typeof issue === "object") {
        add(issue.code ?? "", issue.detail ?? "");
      }
    }
  }

  if (problem.errors && typeof problem.errors === "object") {
    for (const [field, entry] of Object.entries(problem.errors)) {
      (Array.isArray(entry) ? entry : [entry]).forEach((message) => add(field, message));
    }
  }

  return fieldMessages;
}

function getDefaultErrorTitle(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "Request could not be completed";
    case 401:
      return "Sign-in required";
    case 403:
      return "Access denied";
    case 404:
      return "Requested record was not found";
    case 409:
      return "Conflict detected.";
    default:
      return "Unexpected error";
  }
}

function getDefaultErrorMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return "Review the highlighted values and try again.";
    case 401:
      return "Your session is no longer authenticated. Sign in again and retry the action.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested user assignment or navigation record could not be found.";
    case 409:
      return "The requested change conflicts with the current lifecycle state.";
    default:
      return "The request could not be completed. Try again or contact support if the problem persists.";
  }
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
